// ═══════════════════════════════════════════════════════════════════════
// Congés › Droits à congé (Lot 3, moteur Congés) — vue DRH de tous les salariés actifs.
// Alertes agrégées (une phrase par sujet), filtres avec leur nombre, tableau des compteurs et
// état du calcul en mots simples. Les droits sont contrôlés par le serveur (/api/droits-conges) :
// un refus s'affiche tel quel. Aucun code technique à l'écran.
// Ce fichier exporte aussi les petits utilitaires partagés avec la fiche d'un salarié.
// ═══════════════════════════════════════════════════════════════════════
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CalendarRange, AlertTriangle, ChevronRight, RefreshCw } from 'lucide-react';
import Layout from '../../components/Layout';
import { EnTetePage, Carte, Bouton, BandeauInfo, Badge, Squelette, EtatVide, ChampRecherche, formaterDate } from '../../design/composants';
import { useChargement } from '../temps/reglagesCommun';

// ─── Accès à l'API du moteur Congés ──────────────────────────────────────────────────────────
const API = '/api/droits-conges';
export const lireDroits = async (url, params) => (await axios.get(`${API}${url}`, { params })).data.data;
export const ecrireDroits = async (url, corps = {}) => (await axios.post(`${API}${url}`, corps)).data.data;
export const messageErreur = (e, defaut = 'Opération impossible. Réessayez.') => e?.response?.data?.message || defaut;

// ─── Formats ─────────────────────────────────────────────────────────────────────────────────
const FMT_NOMBRE = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });
/** Nombre à la française (virgule décimale). */
export const nombre = (n) => (n === null || n === undefined || Number.isNaN(Number(n)) ? '—' : FMT_NOMBRE.format(Number(n)));
/** Jours : « 2,5 j ». */
export const jours = (n) => (n === null || n === undefined ? '—' : `${nombre(n)} j`);
/** Date JJ/MM/AAAA ou tiret. */
export const date = (d) => formaterDate(d) || '—';
/** Aujourd'hui (date locale) au format AAAA-MM-JJ. */
export const aujourdhui = () => {
  const x = new Date();
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, '0')}-${String(x.getDate()).padStart(2, '0')}`;
};

// ─── Libellés (codes du serveur → phrases simples) ───────────────────────────────────────────
export const ETAT_CALCUL = {
  CERTAIN: { ton: 'succes', libelle: 'Calcul certain', aide: 'Toutes les règles et données utiles sont confirmées.' },
  SOUS_RESERVE: { ton: 'alerte', libelle: 'Sous réserve', aide: 'Une donnée ou une règle reste à confirmer : le résultat est indicatif.' },
  NON_CALCULABLE: { ton: 'danger', libelle: 'Non calculable', aide: 'Une règle indispensable ou une donnée manque : aucun droit ne peut être calculé.' },
};
export const BadgeEtatCalcul = ({ qualite, title }) => {
  const e = ETAT_CALCUL[qualite] || { ton: 'neutre', libelle: 'Inconnu' };
  return <Badge ton={e.ton} point title={title || e.aide}>{e.libelle}</Badge>;
};

export const QUALITES_DATE = {
  CONFIRMEE: 'Confirmée', REPRISE_APPROXIMATIVE: 'Reprise approximative', A_CONFIRMER: 'À confirmer', INCONNUE: 'Inconnue',
  PROBABLE_REELLE: 'Probablement réelle, non confirmée', IMPOSSIBLE_A_DETERMINER: 'Impossible à déterminer', MANQUANTE: 'Absente',
};
export const SOURCES_QUALITE_DATE = { QUALIFICATION_RH: 'qualifiée par la RH', PRESOMPTION: 'estimée automatiquement' };
export const libelleQualiteDate = (q) => (q && q.valeur ? QUALITES_DATE[q.valeur] || 'Non qualifiée' : 'Non qualifiée');
export const BadgeQualiteDate = ({ q }) => (
  <Badge ton={q && q.valeur === 'CONFIRMEE' ? 'succes' : 'alerte'} title={q && q.source ? `Qualité ${SOURCES_QUALITE_DATE[q.source] || ''}` : undefined}>
    {libelleQualiteDate(q)}
  </Badge>
);

export const RESERVES = {
  DATE_ENTREE_A_CONFIRMER: 'La date d\'entrée n\'est pas confirmée par la RH.',
  HISTORIQUE_NON_REPRIS: 'L\'historique antérieur n\'est pas repris : la reprise des soldes reste à faire.',
  REGLE_A_CONFIRMER: 'Une règle appliquée n\'est pas encore confirmée.',
  SERVICE_EFFECTIF_NON_REGLE: 'La règle du service effectif n\'est pas réglée : toutes les périodes sont comptées.',
  SERVICE_EFFECTIF_A_CONFIRMER: 'Une absence a un traitement à confirmer par le juriste.',
  ABSENCE_NON_CLASSEE: 'Une absence n\'est pas classée (service effectif ou non).',
  PRORATA_NON_REGLE: 'La règle du mois commencé n\'est pas décidée.',
  ANCIENNETE_A_CONFIRMER: 'L\'ancienneté est à confirmer : la majoration n\'est pas accordée.',
  ENFANTS_DONNEE_INSUFFISANTE: 'Les enfants ne sont pas tous détaillés : la majoration mère de famille ne peut pas être calculée.',
  GENRE_MANQUANT: 'Le genre n\'est pas renseigné.',
  DATE_NAISSANCE_MANQUANTE: 'La date de naissance n\'est pas renseignée.',
  REPORT_NON_REGLE: 'La règle de report n\'est pas réglée : aucune échéance n\'est calculée.',
};
export const BLOCAGES = {
  DATE_ENTREE_MANQUANTE: 'La date d\'entrée est absente.',
  REGLE_MANQUANTE: 'Une règle indispensable n\'est pas validée.',
  MODE_PERIODE_NON_PRIS_EN_CHARGE: 'Une période de référence collective est validée : ce calcul ne la prend pas en charge.',
};
export const ALERTES = {
  ECHEANCE_PROCHE: 'Un droit arrive bientôt à échéance.',
  ECHEANCE_DEPASSEE: 'L\'échéance d\'un droit est dépassée : à régulariser.',
  DROIT_A_OUVRIR: 'Un droit calculé attend son ouverture.',
  DROIT_A_CONFIRMER: 'Un droit sous réserve attend la confirmation de la RH.',
  DATE_ENTREE_MODIFIEE: 'La date d\'entrée a changé depuis sa qualification.',
};
const listePhrases = (codes, dico) => [...new Set(codes || [])].map((c) => dico[c] || 'Point à vérifier.');

export const FILTRES = {
  droits_ouverts: 'Droits ouverts', en_acquisition: 'En acquisition', donnees_a_confirmer: 'Données à confirmer', prochain_depart: 'Départ en congé prévu',
  echeance_proche: 'Échéance proche', reprise_necessaire: 'Reprise des soldes à faire', anciennete_seras: 'Ancienneté à décider', date_incertaine: 'Date d\'entrée non confirmée',
};
const TONS_ALERTE = { ECHEANCE_DEPASSEE: 'danger', NON_CALCULABLE: 'danger', ECHEANCE_PROCHE: 'alerte' };
const FILTRE_ALERTE = {
  ECHEANCE_PROCHE: 'echeance_proche', ECHEANCE_DEPASSEE: 'echeance_proche', ANCIENNETE_SERAS: 'anciennete_seras', REPRISE_NECESSAIRE: 'reprise_necessaire',
  DROITS_A_CONFIRMER: 'donnees_a_confirmer', DATES_A_CONFIRMER: 'date_incertaine', NON_CALCULABLE: 'donnees_a_confirmer',
};

/** Phrase lisible d'une alerte agrégée, avec l'accord du nombre (« 1 salarié a », « 12 salariés ont »). */
function phraseAlerte(a) {
  const n = a.nombre;
  const s = n > 1;
  const sal = `${n} salarié${s ? 's' : ''}`;
  switch (a.code) {
    case 'ECHEANCE_PROCHE': return `${sal} ${s ? 'ont' : 'a'} un droit arrivant bientôt à échéance.`;
    case 'ECHEANCE_DEPASSEE': return `${sal} ${s ? 'ont' : 'a'} un droit dont l'échéance est dépassée : à régulariser (rien n'est supprimé).`;
    case 'ANCIENNETE_SERAS': return `${sal} ${s ? 'attendent' : 'attend'} une décision sur l'ancienneté reprise.`;
    case 'REPRISE_NECESSAIRE': return `${sal} ${s ? 'attendent' : 'attend'} la reprise de leur situation de congés.`;
    case 'DROITS_A_CONFIRMER': return `${sal} ${s ? 'ont' : 'a'} un droit sous réserve à confirmer.`;
    case 'DATES_A_CONFIRMER': return `${n} date${s ? 's' : ''} d'entrée ${s ? 'ne sont pas confirmées' : 'n\'est pas confirmée'} par la RH.`;
    case 'NON_CALCULABLE': return `${sal} : droit non calculable (règle non validée ou donnée manquante).`;
    default: return a.message;
  }
}

/** Info-bulle de l'état : réserves, blocages et alertes en phrases. */
function detailEtat(l) {
  const phrases = [...listePhrases(l.blocages, BLOCAGES), ...listePhrases(l.reserves, RESERVES), ...listePhrases(l.alertes, ALERTES)];
  if (l.droits_a_confirmer) phrases.push(`${l.droits_a_confirmer} droit${l.droits_a_confirmer > 1 ? 's' : ''} à confirmer.`);
  return phrases.length ? phrases.join(' ') : ETAT_CALCUL[l.qualite]?.aide;
}

const normaliser = (t) => String(t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

export default function DroitsCongesPage() {
  const [params, setParams] = useSearchParams();
  const filtre = params.get('filtre') || '';
  const dateRef = params.get('date') || '';
  const [recherche, setRecherche] = useState('');
  const { donnees, erreur, recharger } = useChargement(
    () => lireDroits('', { ...(filtre ? { filtre } : {}), ...(dateRef ? { date: dateRef } : {}) }),
    [filtre, dateRef],
  );
  const changer = (cle, valeur) => {
    const p = new URLSearchParams(params);
    if (valeur) p.set(cle, valeur); else p.delete(cle);
    setParams(p, { replace: true });
  };

  const lignes = useMemo(() => {
    const toutes = donnees?.lignes || [];
    const q = normaliser(recherche.trim());
    if (!q) return toutes;
    return toutes.filter((l) => normaliser(`${l.employe.nom} ${l.employe.matricule || ''} ${l.employe.site || ''}`).includes(q));
  }, [donnees, recherche]);

  return (
    <Layout>
      <div className="rh-page" style={{ maxWidth: '100%', overflowX: 'hidden' }}>
        <EnTetePage fil={[{ libelle: 'Congés', vers: '/conges' }, { libelle: 'Droits à congé' }]}
          titre="Droits à congé"
          sousTitre={donnees ? `Situation au ${date(donnees.date_reference)} — salariés actifs` : 'Situation des droits de chaque salarié actif'}
          actions={<Bouton Icone={RefreshCw} onClick={recharger}>Actualiser</Bouton>} />

        {erreur && <BandeauInfo ton="danger" titre="Affichage impossible">{erreur}</BandeauInfo>}

        {donnees && donnees.alertes.length > 0 && (
          <Carte titre="À surveiller" Icone={AlertTriangle}>
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: 6 }}>
              {donnees.alertes.map((a) => (
                <li key={a.code} style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap', fontSize: 14 }}>
                  <Badge ton={TONS_ALERTE[a.code] || 'info'}>{nombre(a.nombre)}</Badge>
                  <span style={{ flex: '1 1 240px', minWidth: 0 }}>{phraseAlerte(a)}</span>
                  {FILTRE_ALERTE[a.code] && filtre !== FILTRE_ALERTE[a.code] && (
                    <Bouton petit variante="lien" onClick={() => changer('filtre', FILTRE_ALERTE[a.code])}>Voir ces salariés</Bouton>
                  )}
                </li>
              ))}
            </ul>
          </Carte>
        )}
        {donnees && donnees.alertes.length === 0 && (
          <BandeauInfo ton="succes">Aucun point à surveiller pour le moment.</BandeauInfo>
        )}

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', margin: '12px 0' }} role="group" aria-label="Filtres">
          <Bouton petit variante={!filtre ? 'primaire' : 'secondaire'} aria-pressed={!filtre} onClick={() => changer('filtre', '')}>Tous</Bouton>
          {(donnees?.filtres || Object.keys(FILTRES).map((code) => ({ code, nombre: null }))).map((f) => (
            <Bouton key={f.code} petit variante={filtre === f.code ? 'primaire' : 'secondaire'} aria-pressed={filtre === f.code}
              onClick={() => changer('filtre', filtre === f.code ? '' : f.code)}>
              {FILTRES[f.code] || 'Autre'}{f.nombre !== null && <span style={{ opacity: 0.8 }}>({nombre(f.nombre)})</span>}
            </Bouton>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <div style={{ flex: '1 1 260px', minWidth: 0 }}>
            <ChampRecherche valeur={recherche} onChange={setRecherche} placeholder="Nom, matricule ou site" libelle="Rechercher un salarié" />
          </div>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 14, flexWrap: 'wrap' }}>
            Situation au
            <input type="date" className="rh-champ-saisie" style={{ width: 'auto' }} value={dateRef || ''} onChange={(e) => changer('date', e.target.value)} />
          </label>
        </div>

        <Carte titre={donnees ? `${nombre(lignes.length)} salarié${lignes.length > 1 ? 's' : ''}${filtre ? ` — ${FILTRES[filtre] || ''}` : ''}` : 'Salariés'} Icone={CalendarRange}>
          {!donnees && !erreur && <div style={{ display: 'grid', gap: 8 }}>{[0, 1, 2, 3, 4].map((i) => <Squelette key={i} hauteur={28} />)}</div>}
          {donnees && lignes.length === 0 && <EtatVide titre="Aucun salarié" texte={filtre || recherche ? 'Aucun salarié ne correspond à ce filtre.' : 'Aucun salarié actif.'} />}
          {donnees && lignes.length > 0 && (
            <div style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }} tabIndex={0} role="region" aria-label="Tableau des droits, défilement horizontal possible">
              <table className="rh-tableau" style={{ minWidth: 1080, fontSize: 14 }}>
                <thead>
                  <tr>
                    <th>Salarié</th><th>Site</th><th>Date d'entrée</th><th>En acquisition</th><th style={{ textAlign: 'right' }}>Droits ouverts</th>
                    <th style={{ textAlign: 'right' }}>Restants</th><th>Prochaine échéance</th><th>Prochain départ</th><th>État</th><th aria-label="Fiche" />
                  </tr>
                </thead>
                <tbody>
                  {lignes.map((l) => {
                    const c = l.compteurs;
                    return (
                      <tr key={l.employe.id}>
                        <td>
                          <Link to={`/conges/droits/${l.employe.id}`} className="rh-cellule-principale">{l.employe.nom}</Link>
                          {l.employe.matricule && <div className="rh-cellule-secondaire">Matricule {l.employe.matricule}</div>}
                        </td>
                        <td>{l.employe.site || '—'}</td>
                        <td>
                          <div>{date(l.employe.date_entree)}</div>
                          <div className="rh-cellule-secondaire">{libelleQualiteDate(l.employe.qualite_date_entree)}</div>
                        </td>
                        <td>
                          <div>{l.en_acquisition ? jours(l.en_acquisition.jours) : '—'}</div>
                          {l.en_acquisition?.ouverture_prevue && <div className="rh-cellule-secondaire">utilisables le {date(l.en_acquisition.ouverture_prevue)}</div>}
                        </td>
                        <td style={{ textAlign: 'right' }}>{l.solde_a_verifier ? 'À vérifier' : jours(c.disponibles)}</td>
                        <td style={{ textAlign: 'right', fontWeight: 600 }}>{l.solde_a_verifier ? 'À vérifier' : jours(c.restants)}</td>
                        <td>
                          {c.prochaine_echeance ? date(c.prochaine_echeance) : '—'}
                          {(l.alertes || []).includes('ECHEANCE_DEPASSEE') && <div><Badge ton="danger">Dépassée</Badge></div>}
                          {!(l.alertes || []).includes('ECHEANCE_DEPASSEE') && (l.alertes || []).includes('ECHEANCE_PROCHE') && <div><Badge ton="alerte">Échéance proche</Badge></div>}
                        </td>
                        <td>{c.prochain_depart ? date(c.prochain_depart) : '—'}</td>
                        <td><BadgeEtatCalcul qualite={l.qualite} title={detailEtat(l)} />
                          {l.droits_a_confirmer > 0 && <div className="rh-cellule-secondaire">{l.droits_a_confirmer} droit{l.droits_a_confirmer > 1 ? 's' : ''} à confirmer</div>}
                        </td>
                        <td>
                          <Link to={`/conges/droits/${l.employe.id}`} className="rh-btn rh-btn-petit rh-btn-discret" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}
                            aria-label={`Ouvrir la fiche des droits de ${l.employe.nom}`}>
                            Fiche<ChevronRight size={14} aria-hidden="true" />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Carte>
        <p style={{ fontSize: 13, color: 'var(--sg-texte-2)', margin: '8px 0 0' }}>
          « En acquisition » : jours gagnés mais pas encore utilisables. « Droits ouverts » : total des droits ouverts, avant consommation. « Restants » : ce qu'il reste à prendre, jours pris ou posés et demandes en attente déduits.
        </p>
      </div>
    </Layout>
  );
}
